import { DateField, TypeaheadField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_LEVEL, IOptionItem } from '@wyz/types';
import {
  Accordion,
  Alert,
  Card,
  CenteredSpinner,
  Col,
  FormContainer,
  Icon,
  notify,
  Row,
  Text,
  Title,
  Flex,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { isNil } from 'lodash-es';
import * as React from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { usePricingPolicyForm } from '~/purchase/components';
import { QuotePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICING_STATUS_ENUM,
  quoteDefaultPricingApplicationStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalDefaultQuotePricingFormData,
  IPricingStatus,
  IQuoteDefaultPricingApplicationStepForm,
} from '~/purchase/types';
import { usePricingPolicyErrorTranslation } from '~/common/hooks';
import { useManageQuotePricingPolicies } from '~/purchase/api';
import { useManageCustomers } from '~/common/api';

type CommonFormProps = {
  globalFormData: IGlobalDefaultQuotePricingFormData;
  policyGroupId?: number;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuoteDefaultPricingApplicationStepForm,
    status: IPricingStatus,
  ) => void;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  globalFormData,
  policyGroupId,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  const pricingPolicyForm = usePricingPolicyForm();
  const manageCustomers = useManageCustomers({
    autocomplete: { enabled: true },
  });
  const methods = useForm<IQuoteDefaultPricingApplicationStepForm>({
    defaultValues: globalFormData.APPLICATION,
    resolver: zodResolver(quoteDefaultPricingApplicationStepFormSchema),
  });
  const watchedStartDate = useWatch({
    control: methods.control,
    name: 'startDate',
  });

  const manageQuotePricingPolicies = useManageQuotePricingPolicies({
    checkOverlap: { enabled: true },
  });

  return (
    <FormContainer
      className='space-y-4'
      methods={methods}
      submitHandler={(formData, event) => {
        manageQuotePricingPolicies.checkOverlap.mutate(
          {
            policyGroupId: policyGroupId,
            customerIds: [...formData.customers],
            startDate: DateUtils.formatTimestamp(formData.startDate, {
              customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
            }),
            endDate: formData.endDate
              ? DateUtils.formatTimestamp(formData.endDate, {
                  customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
                })
              : null,
            default: true,
          },
          {
            onError: (error) => {
              notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
                id: 'toast-error-quote-default-pricing-overlap-check-failed',
              });
            },
            onSuccess: () => {
              onSubmit(
                formData,
                ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
                  'data-status',
                ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
              );
            },
          },
        );
      }}
      id={QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX.APPLICATION.toString()}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
    >
      {pricingPolicyForm.mode !== 'READ_ONLY' && (
        <Alert variant='info'>
          <Flex>
            <Icon icon='information-line' />
            <div>
              <Text weight='bolder' color='info'>
                <Trans i18nKey='app.pricing_policy.redistribution.form.application_step.info.title' />
              </Text>
              <Text weight='normal' color='info'>
                <Trans i18nKey='app.pricing_policy.redistribution.form.application_step.info.subtitle' />
              </Text>
            </div>
          </Flex>
        </Alert>
      )}
      <Card>
        {manageQuotePricingPolicies.checkOverlap.isLoading && (
          <div
            className='position-absolute h-100 w-100'
            style={{ backgroundColor: 'rgba(0,0,0,.1)', zIndex: 5 }}
          >
            <div className='center-v-h'>
              <CenteredSpinner />
            </div>
          </div>
        )}
        <Card.Body>
          <Row>
            <Col sm={12} md={6}>
              <DateField
                name='startDate'
                label={
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.start_date' />
                }
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            {watchedStartDate && (
              <Col sm={12} md={6}>
                <DateField
                  name='endDate'
                  label={
                    <Trans i18nKey='app.pricing_policy.redistribution.form.application.end_date' />
                  }
                  dateFormat={DateUtils.DATE_FORMAT.DMY}
                  placeholderText='dd-MM-yyyy'
                  showTimeSelect={false}
                  minDate={
                    isNil(watchedStartDate) ? undefined : watchedStartDate
                  }
                  translations={{
                    applyButton: t('app.common.button.apply'),
                    resetButton: t('app.common.button.reset'),
                  }}
                />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Accordion.Container flush defaultActiveKey='0'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <Title heading='xl' weight='bolder'>
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.customers.title' />
                </Title>
              </Accordion.Header>
              <Accordion.Body>
                <TypeaheadField<true, IOptionItem<string, string>>
                  required={false}
                  isClearable
                  minCharacter={3}
                  maxShown={10}
                  minCharacterMessage={
                    <Trans
                      i18nKey='app.common.autocomplete.min_character_message'
                      values={{
                        minCharacter: 3,
                      }}
                    />
                  }
                  name={'customersAutocomplete'}
                  label={
                    <Trans i18nKey='app.pricing_policy.redistribution.form.application.customers.title' />
                  }
                  fetcher={(query, options) =>
                    manageCustomers.autocomplete(query, {
                      ...options,
                      authorizedLevels: [CUSTOMER_LEVEL.N2, CUSTOMER_LEVEL.N1],
                    })
                  }
                  onSelect={(selected) => {
                    methods.setValue(
                      'customers',
                      selected.map((item) => item.value),
                    );
                  }}
                  isMulti
                  wrapItems
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion.Container>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default ApplicationStepForm;
